import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ComparisonFilterPanel from '../../components/reports/ComparisonFilterPanel';
import {
  comparisonDashboardService,
  ComparisonFilters,
  ComparisonSummary,
  ComparisonWidget,
  GroupByDimension,
} from '../../services/comparisonDashboardService';

function getDefaultDateRange(): { start_date: string; end_date: string } {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const end_date = yesterday.toISOString().split('T')[0];

  const start = new Date(yesterday);
  start.setFullYear(start.getFullYear() - 1);
  start.setDate(start.getDate() + 1);
  const start_date = start.toISOString().split('T')[0];

  return { start_date, end_date };
}

export { getDefaultDateRange };

const GROUP_BY_OPTIONS: { value: GroupByDimension; label: string }[] = [
  { value: 'product', label: 'Product' },
  { value: 'customer', label: 'Customer' },
  { value: 'brand', label: 'Brand' },
  { value: 'category', label: 'Category' },
  { value: 'sales_rep', label: 'Sales Rep' },
  { value: 'territory', label: 'Territory' },
];

const SECONDARY_LABEL: Record<GroupByDimension, string> = {
  product: 'Customer',
  customer: 'Product',
  brand: 'Customer',
  category: 'Product',
  sales_rep: 'Product',
  territory: 'Product',
};

const METRIC_COLUMNS = [
  { key: 'revenue', label: 'Revenue', format: 'currency' },
  { key: 'qty', label: 'Qty', format: 'number' },
  { key: 'avg_sale', label: 'Avg Sale', format: 'currency' },
  { key: 'min_sale', label: 'Min Sale', format: 'currency' },
  { key: 'max_sale', label: 'Max Sale', format: 'currency' },
  { key: 'avg_price', label: 'Avg Price', format: 'currency' },
  { key: 'min_price', label: 'Min Price', format: 'currency' },
  { key: 'max_price', label: 'Max Price', format: 'currency' },
] as const;

function formatValue(value: number, format: string): string {
  if (format === 'currency') {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }
  return value.toLocaleString('en-US', { maximumFractionDigits: 0 });
}

const FILTER_KEYS = [
  'brands',
  'categories',
  'products',
  'customers',
  'sales_reps',
  'territories',
] as const;

function filtersToParams(filters: ComparisonFilters): URLSearchParams {
  const params = new URLSearchParams();
  for (const key of FILTER_KEYS) {
    const val = filters[key];
    if (val?.length) params.set(key, val.join(','));
  }
  if (filters.start_date) params.set('start_date', filters.start_date);
  if (filters.end_date) params.set('end_date', filters.end_date);
  return params;
}

function paramsToFilters(params: URLSearchParams): ComparisonFilters {
  const defaults = getDefaultDateRange();
  const f: ComparisonFilters = {};
  for (const key of FILTER_KEYS) {
    const val = params.get(key);
    if (val) (f as any)[key] = val.split(',').map((s) => s.trim());
  }
  f.start_date = params.get('start_date') || defaults.start_date;
  f.end_date = params.get('end_date') || defaults.end_date;
  return f;
}

function filtersEmpty(f: ComparisonFilters): boolean {
  return (
    !f.brands?.length &&
    !f.categories?.length &&
    !f.products?.length &&
    !f.customers?.length &&
    !f.sales_reps?.length &&
    !f.territories?.length
  );
}

const ComparisonDashboardPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<ComparisonFilters>(() => paramsToFilters(searchParams));
  const [groupBy, setGroupBy] = useState<GroupByDimension>(
    () => (searchParams.get('group_by') as GroupByDimension) || 'product'
  );

  const [summary, setSummary] = useState<ComparisonSummary | null>(null);
  const [widgets, setWidgets] = useState<ComparisonWidget[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingWidgets, setLoadingWidgets] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  const syncUrl = useCallback(
    (f: ComparisonFilters, gb: GroupByDimension) => {
      const params = filtersToParams(f);
      params.set('group_by', gb);
      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  const loadData = useCallback(async (f: ComparisonFilters, gb: GroupByDimension) => {
    if (filtersEmpty(f)) {
      setSummary(null);
      setWidgets([]);
      return;
    }

    setError(null);
    setLoadingSummary(true);
    setLoadingWidgets(true);

    try {
      const [summaryData, widgetData] = await Promise.all([
        comparisonDashboardService.getSummary(f),
        comparisonDashboardService.getComparisonData(f, gb),
      ]);
      setSummary(summaryData);
      setWidgets(widgetData);
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
      setSummary(null);
      setWidgets([]);
    } finally {
      setLoadingSummary(false);
      setLoadingWidgets(false);
    }
  }, []);

  const handleFiltersChange = useCallback(
    (newFilters: ComparisonFilters) => {
      setFilters(newFilters);
      syncUrl(newFilters, groupBy);
      loadData(newFilters, groupBy);
    },
    [groupBy, syncUrl, loadData]
  );

  const handleGroupByChange = useCallback(
    (gb: GroupByDimension) => {
      setGroupBy(gb);
      syncUrl(filtersRef.current, gb);
      loadData(filtersRef.current, gb);
    },
    [syncUrl, loadData]
  );

  useEffect(() => {
    if (!filtersEmpty(filters)) {
      loadData(filters, groupBy);
    }
  }, []);

  const isLoading = loadingSummary || loadingWidgets;
  const hasFilters = !filtersEmpty(filters);

  return (
    <div className="page-container">
      <style>{`
        @keyframes comparison-spin {
          0% { transform: translateY(-50%) rotate(0deg); }
          100% { transform: translateY(-50%) rotate(360deg); }
        }
      `}</style>

      <div className="page-header">
        <h1 className="page-title">Comparison Dashboard</h1>
        <p className="page-subtitle">
          Analyze and compare sales performance across multiple dimensions
        </p>
      </div>

      <ComparisonFilterPanel
        filters={filters}
        onFiltersChange={handleFiltersChange}
        loading={isLoading}
      />

      {!hasFilters && (
        <div style={styles.emptyState}>
          <p style={styles.emptyText}>Select filters above to begin comparing sales data.</p>
        </div>
      )}

      {error && <div style={styles.errorBanner}>{error}</div>}

      {hasFilters && (
        <>
          {/* Summary Panel */}
          <div style={styles.summaryRow}>
            <SummaryTile
              label="Revenue"
              value={summary?.revenue}
              format="currency"
              loading={loadingSummary}
            />
            <SummaryTile
              label="Qty (Cases)"
              value={summary?.qty}
              format="number"
              loading={loadingSummary}
            />
            <SummaryTile
              label="Active Customers"
              value={summary?.active_customers}
              format="number"
              loading={loadingSummary}
            />
            <SummaryTile
              label="Orders"
              value={summary?.orders}
              format="number"
              loading={loadingSummary}
            />
          </div>

          {/* Group By Selector */}
          <div style={styles.groupByRow}>
            <span style={styles.groupByLabel}>Group By:</span>
            <div style={styles.groupByButtons}>
              {GROUP_BY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => handleGroupByChange(opt.value)}
                  style={{
                    ...styles.groupByBtn,
                    ...(groupBy === opt.value ? styles.groupByBtnActive : {}),
                  }}
                  disabled={isLoading}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Widgets */}
          {loadingWidgets ? (
            <div style={styles.loadingBox}>Loading comparison data...</div>
          ) : widgets.length === 0 ? (
            <div style={styles.emptyState}>
              <p style={styles.emptyText}>No data found for the selected filters.</p>
            </div>
          ) : (
            <div style={styles.widgetGrid}>
              {widgets.map((w) => (
                <WidgetCard
                  key={w.group_key}
                  widget={w}
                  secondaryLabel={SECONDARY_LABEL[groupBy]}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

/* --- Sub-components --- */

const SummaryTile: React.FC<{
  label: string;
  value: number | undefined;
  format: 'currency' | 'number';
  loading: boolean;
}> = ({ label, value, format, loading }) => (
  <div style={styles.tile}>
    <div style={styles.tileLabel}>{label}</div>
    <div style={styles.tileValue}>
      {loading ? '...' : value !== undefined ? formatValue(value, format) : '--'}
    </div>
  </div>
);

const WidgetCard: React.FC<{
  widget: ComparisonWidget;
  secondaryLabel: string;
}> = ({ widget, secondaryLabel }) => (
  <div style={styles.widgetCard}>
    <div style={styles.widgetHeader}>{widget.group_label}</div>
    <div style={styles.tableWrapper}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={{ ...styles.th, ...styles.thFirst }}>{secondaryLabel}</th>
            {METRIC_COLUMNS.map((col) => (
              <th key={col.key} style={styles.th}>
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {widget.rows.map((row) => (
            <tr key={row.row_key}>
              <td style={{ ...styles.td, fontWeight: 500 }}>{row.row_label}</td>
              {METRIC_COLUMNS.map((col) => (
                <td key={col.key} style={styles.tdNum}>
                  {formatValue((row as any)[col.key], col.format)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

/* --- Styles --- */

const styles: Record<string, React.CSSProperties> = {
  emptyState: {
    textAlign: 'center',
    padding: '3rem 1rem',
    color: '#6b7280',
  },
  emptyText: {
    fontSize: '1.05rem',
  },
  errorBanner: {
    margin: '1rem',
    padding: '0.75rem 1rem',
    backgroundColor: '#fef2f2',
    color: '#991b1b',
    borderRadius: '6px',
    border: '1px solid #fecaca',
    fontSize: '0.9rem',
  },
  summaryRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '1rem',
    padding: '1rem 1.25rem',
  },
  tile: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    textAlign: 'center',
  },
  tileLabel: {
    fontSize: '0.8rem',
    fontWeight: 600,
    color: '#6b7280',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.05em',
    marginBottom: '0.4rem',
  },
  tileValue: {
    fontSize: '1.5rem',
    fontWeight: 700,
    color: '#111827',
  },
  groupByRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0 1.25rem 0.75rem',
    flexWrap: 'wrap' as const,
  },
  groupByLabel: {
    fontWeight: 600,
    fontSize: '0.9rem',
    color: '#374151',
  },
  groupByButtons: {
    display: 'flex',
    gap: '0.35rem',
    flexWrap: 'wrap' as const,
  },
  groupByBtn: {
    padding: '0.4rem 0.85rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: 'white',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: '#374151',
    transition: 'all 0.15s',
  },
  groupByBtnActive: {
    background: '#3b82f6',
    color: 'white',
    borderColor: '#3b82f6',
  },
  loadingBox: {
    textAlign: 'center',
    padding: '2rem',
    color: '#6b7280',
    fontSize: '0.95rem',
  },
  widgetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))',
    gap: '1rem',
    padding: '0 1.25rem 1.5rem',
  },
  widgetCard: {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  widgetHeader: {
    padding: '0.75rem 1rem',
    fontWeight: 600,
    fontSize: '1.05rem',
    color: '#1f2937',
    borderBottom: '1px solid #e5e7eb',
    background: '#f9fafb',
  },
  tableWrapper: {
    overflowX: 'auto' as const,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
  },
  th: {
    padding: '0.5rem 0.65rem',
    textAlign: 'right' as const,
    fontWeight: 600,
    color: '#6b7280',
    borderBottom: '2px solid #e5e7eb',
    whiteSpace: 'nowrap' as const,
    fontSize: '0.8rem',
  },
  thFirst: {
    textAlign: 'left' as const,
  },
  td: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
  },
  tdNum: {
    padding: '0.5rem 0.65rem',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151',
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums',
  },
};

export default ComparisonDashboardPage;
